import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { Subscription } from "rxjs";

import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  BscCiclo,
  BscEjercicio,
  BscEjercicioKpi,
  BscEjercicioTrimestre,
  BscHistorial,
  BscKpiMes,
  BscKpiTrimestre,
  BscRes,
  EstadoMesBsc,
  EstatusTrimestreBsc,
  SemaforoBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { BscService } from "../bsc.service";
import { FraccionesBarra, PistaBarra, VERDE_POR_OMISION, enPista, escalaBarras, ratio } from "../bsc-calculos";
import { ACCION_HISTORIAL, ESTADO_MES, ESTATUS_TRIMESTRE, EstadoBsc, SEMAFORO } from "../bsc-estados";
import {
  SIN_DATO,
  diaMes,
  fechaHora,
  mesEnTexto,
  mesesDeTrimestre,
  nombreMes,
  numero,
  pct,
  pts,
  unidad,
} from "../bsc-formato";
import { acumProgEn, acumRealEn, aplicaEn, pesoEn, ponderadoEn, semaforoEn } from "../bsc-kpi";
import {
  ComentarioDialogoPinBsc,
  DialogoPinBscComponent,
  abrirDialogoPinBsc,
} from "../dialogo-pin/dialogo-pin-bsc.component";

/** Lo que avisa el detalle a la bandeja: hay que volver a pedirla. */
export interface CambioEnvioBsc {
  /** true = se autorizó, regresó o reabrió; false = el envío cambió por otro lado (conflicto) y se recargó. */
  resuelto: boolean;
  /** El renglón como quedó, si se resolvió. */
  trimestre?: BscEjercicioTrimestre;
}

/**
 *   VACIO     no hay envío elegido (la bandeja está vacía o filtrada sin renglones);
 *   CARGANDO  detalle.do en vuelo (con el renglón de la bandeja en la cabecera);
 *   ERROR     detalle.do falló (otra empresa, sin la hoja, sin sesión, sin conexión);
 *   DATOS     el envío.
 */
type EstadoDetalle = "VACIO" | "CARGANDO" | "ERROR" | "DATOS";

/** Lo que se hace con el trimestre: resolver el ENVIADO o reabrir el AUTORIZADO. */
type ModoAccion = "RESOLVER" | "REABRIR";

/**
 * La barra de un KPI: el acumulado real del año (fracciones) con la raya de
 * lo programado; `pista`, dónde se pinta con la escala de los KPIs del trimestre.
 */
interface BarraKpi extends FraccionesBarra {
  pista: PistaBarra;
  /** is-verde | is-amarillo | is-rojo | "" */
  clase: string;
  texto: string;
  accesible: string;
}

/** Un mes del KPI en el trimestre (la evidencia que revisa Dirección). */
interface MesEnvio {
  mes: number;
  nombre: string;
  estado: EstadoBsc<EstadoMesBsc> | null;
  actividad: string;
  evidencia: string;
  capturado: boolean;
  /** "Capturó Ana López · 26 sep"; vacío sin captura. */
  capturo: string;
  /** "Sin captura", "No aplica en este trimestre"… */
  vacio: string;
}

/** Un KPI del trimestre, armado UNA vez por respuesta. */
interface FilaIndicador {
  idEjercicioKpi: number;
  objetivo: string;
  /** Aplica en el trimestre (los NA se listan sin barra). */
  vigente: boolean;
  /** "NA · inicia en Q4" para los que no aplican. */
  nota: string;
  barra: BarraKpi | null;
  semaforo: EstadoBsc<SemaforoBsc> | null;
  bajoMeta: boolean;
  /** "Real 90% · programado 100% · peso 25% · ponderado 22.5%". */
  avance: string;
  recursos: string;
  comentario: string;
  meses: MesEnvio[];
  etiquetaAbrir: string;
}

/** Un KPI por debajo del verde (kpisBajoMeta), con su ratio en palabras (el texto de C3). */
interface IndicadorBajoMeta {
  idEjercicioKpi: number;
  objetivo: string;
  texto: string;
}

/** Una cifra del envío: alcance, programado, KPIs bajo lo programado. */
interface Cifra {
  etiqueta: string;
  valor: string;
  nota: string;
  /** is-aviso cuando hay KPIs bajo lo programado. */
  clase: string;
}

/** Un renglón de la bitácora del trimestre. */
interface Movimiento {
  clave: string;
  cuando: string;
  accion: string;
  quien: string;
  detalle: string;
}

/** Lo que dice la franja de arriba según el estatus. */
interface Franja {
  icono: string;
  texto: string;
  /** is-ok | is-alerta | "" */
  clase: string;
}

/** Las acciones del trimestre con lo que las impide, armado una vez por respuesta. */
interface Acciones {
  modo: ModoAccion | null;
  /** Se pintan los botones (con la llave, el año abierto y un modo). */
  visibles: boolean;
  /** Por qué no hay botones (sin llave, año cerrado, estatus sin acción). */
  nota: string;
  autorizar: boolean;
  /** Por qué Autorizar está deshabilitado (esPropio). */
  porQueNoAutoriza: string;
  reabrir: boolean;
  /** Por qué Reabrir está deshabilitado (Q+1 enviado o autorizado, histórico). */
  porQueNoReabre: string;
}

const BITACORA_CORTA = 6;
const LARGO_COMENTARIO = 1000;

/** Las acciones de la bitácora que mueven el estatus del trimestre. */
const TRANSICIONES = new Set(["CIERRE_Q", "AUTORIZA_Q", "REGRESA_Q", "REAPERTURA_Q"]);

/** Las acciones de la bitácora cuyo comentario lo escribe el sistema, no una persona (como C1). */
const COMENTARIO_DEL_SISTEMA = new Set(["CAPTURA_MES", "CAPTURA_REAL"]);

const MOTIVO_REAPERTURA: ComentarioDialogoPinBsc = {
  obligatorio: true,
  etiqueta: "Motivo de la reapertura",
  ayuda: "Obligatorio: queda en la bitácora y le llega al área.",
  falta: "Escribe por qué se reabre: queda en la bitácora y le llega al área.",
};

/**
 * Lo que el servidor contesta y no se corrige en el cuadro del PIN: el cuadro
 * se cierra al momento y el envío se vuelve a consultar (otro lo resolvió o
 * lo movió, el año se cerró, no es suyo, el trimestre siguiente ya se envió,
 * ya no tiene la llave).
 */
const ERRORES_DE_RECARGA = ["VAL-BSC-CONFLICTO", "VAL-BSC-CERRADO", "VAL-BSC-PROPIA", "VAL-BSC-REVISION", "00002"];

const RUTA = {
  tablero: "/admin/bsc/tablero",
  area: "/admin/bsc/tablero/area",
  kpi: "/admin/bsc/tablero/kpi",
};

/**
 * BSC · C2 el detalle de un envío (docs/BSC.md §4, §7 y Anexo B, maqueta
 * «C2-BandejaRevision», el lado derecho; F6.6). Lo abre la bandeja con el id
 * del renglón; no es una ruta propia.
 *
 * Todo sale de detalle.do (BscRevisionBuss.detalle): el trimestre con su
 * alcance (snapshot si está AUTORIZADO; en vivo si no) y esPropio, la
 * planeación, sus cuatro trimestres, los KPI ACTIVO con K/L/N y semáforo, su
 * avance del trimestre (capturas), sus tres meses con actividad y evidencia,
 * los que quedan bajo el verde (kpisBajoMeta, la regla de C3) y la bitácora
 * del trimestre, más las banderas del área (puedeAutorizar, tienePin).
 *
 * Acciones, con el cuadro del PIN de T3 (abrirDialogoPinBsc, F5.2): ENVIADO
 * → «Autorizar Qn» (congela el alcance y abre Q+1) y «Regresar con
 * comentarios» (comentario obligatorio); AUTORIZADO → «Reabrir Qn» (motivo
 * obligatorio; no si Q+1 ya está enviado o autorizado, ni en un histórico).
 * Sin la llave admin/bsc/autorizar no hay botones y se dice por qué; con el
 * año cerrado, tampoco. Nadie autoriza lo suyo: con esPropio «Autorizar» se
 * deshabilita y se explica; regresar y reabrir sí los puede hacer (decisión
 * (2) de «Resultado de 6.2»). Lo vuelve a exigir el servidor (VAL-BSC-PROPIA
 * antes del PIN). Los errores del PIN los enseña el cuadro; uno que no es del
 * PIN (VAL-BSC-CONFLICTO porque otro ya lo resolvió, VAL-BSC-CERRADO,
 * VAL-BSC-REVISION) cierra el cuadro al llegar (cerrarAnte), se dice aquí y
 * el envío y la bandeja se vuelven a pedir.
 *
 * Base visual: una tarjeta table-page__card con los bloques del módulo
 * (bsc-franja, bsc-mensajes, bsc-nota), la barra de acumulado de A2 y el
 * campo mat-form-field outline de T3.
 */
@Component({
  selector: "app-detalle-envio-bsc",
  standalone: true,
  templateUrl: "./detalle-envio-bsc.component.html",
  styleUrls: ["../bsc.shared.css", "./detalle-envio-bsc.component.css"],
  imports: [CommonModule, FormsModule, RouterLink, MatButtonModule, MatFormFieldModule, MatInputModule],
})
export class DetalleEnvioBscComponent implements OnInit, OnChanges, OnDestroy {
  /** El envío (bsc_ejercicio_trimestre.id); nulo = nada elegido. */
  @Input() idEjercicioTrimestre: number | null = null;
  /** El renglón de la bandeja: la cabecera se pinta mientras llega el detalle. */
  @Input() renglon: BscEjercicioTrimestre | null = null;
  /** El año de la bandeja (para escribir las fechas sin el año cuando es el mismo). */
  @Input() anio: number | null = null;
  @Output() cambio = new EventEmitter<CambioEnvioBsc>();

  readonly ruta = RUTA;
  readonly largoComentario = LARGO_COMENTARIO;
  /** Ajustes, donde cada quien define su PIN (como el cuadro del PIN). */
  readonly rutaAjustes = "#/admin/ajustes";

  estado: EstadoDetalle = "VACIO";
  loading = false;
  error = "";
  /** Lo que pasó al resolver sin éxito (otro se adelantó, el año se cerró): se dice aquí. */
  errorAccion = "";
  /** El comentario para el área (al resolver) o el motivo (al reabrir); viaja al cuadro del PIN. */
  comentario = "";

  trimestre: BscEjercicioTrimestre | null = null;
  ejercicio: BscEjercicio | null = null;
  ciclo: BscCiclo | null = null;
  avisos: string[] = [];
  puedeAutorizar = false;
  tienePin = false;
  verTablero = false;

  /* ---------- Lo que se pinta, armado una vez por respuesta ---------- */
  titulo = "";
  subtitulo = "";
  estatus: EstadoBsc<EstatusTrimestreBsc> | null = null;
  sinEnviar = false;
  semaforo: EstadoBsc<SemaforoBsc> | null = null;
  cifras: Cifra[] = [];
  notaAlcance = "";
  franja: Franja | null = null;
  indicadores: FilaIndicador[] = [];
  bajoMeta: IndicadorBajoMeta[] = [];
  comentarioEnvio = "";
  movimientos: Movimiento[] = [];
  verTodaBitacora = false;
  acciones: Acciones = sinAcciones("");
  /** Los KPI con su evidencia abierta (se conservan al volver a consultar). */
  abiertos = new Set<number>();

  private trimestres: BscEjercicioTrimestre[] = [];
  /** La bandeja pidió el foco con la consulta en vuelo (ver enfocar). */
  private desplazarAlLlegar = false;
  private consulta?: Subscription;
  private dialogo?: MatDialogRef<DialogoPinBscComponent, BscRes>;
  private destruido = false;

  constructor(
    private readonly bscService: BscService,
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly msg: Msg,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.verTablero = tieneAccesoMenu(this.authService.getMenuList(), RUTA.tablero);
  }

  ngOnChanges(cambios: SimpleChanges): void {
    if (cambios["idEjercicioTrimestre"]) {
      const antes = cambios["idEjercicioTrimestre"].previousValue as number | null | undefined;
      if (antes !== this.idEjercicioTrimestre || cambios["idEjercicioTrimestre"].firstChange) {
        this.comentario = "";
        this.errorAccion = "";
        this.abiertos = new Set();
        this.verTodaBitacora = false;
        this.consultar(false);
      }
    } else if (cambios["renglon"] && this.estado !== "DATOS") {
      this.armaCabecera(this.renglon);
    }
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.consulta?.unsubscribe();
    // El cuadro vive en el overlay y sobrevive al cambio de ruta.
    this.dialogo?.close();
  }

  /**
   * La bandeja lleva aquí el foco al elegir un envío. Con el detalle debajo
   * de la lista (pantallas angostas, ver la rejilla de la bandeja) se sube
   * hasta su cabecera; a un lado, solo lo necesario para verla.
   */
  enfocar(): void {
    const titulo = this.host.nativeElement.querySelector<HTMLElement>("#envio-bsc-titulo, #envio-bsc-titulo-vacio");
    if (!titulo) {
      return;
    }
    titulo.focus({ preventScroll: true });
    if (this.loading) {
      // Mientras consulta la tarjeta es corta y la página quizá no alcanza a
      // subirla: se desplaza cuando llega el envío.
      this.desplazarAlLlegar = true;
    } else {
      this.desplaza();
    }
  }

  private desplaza(): void {
    this.desplazarAlLlegar = false;
    const debajo = typeof window.matchMedia === "function" && window.matchMedia("(max-width: 1100px)").matches;
    this.host.nativeElement.scrollIntoView({ block: debajo ? "start" : "nearest" });
  }

  /* ============================ Consulta ============================ */

  /** @param silencioso después de resolver o de un conflicto: lo de ahora se queda mientras contesta. */
  consultar(silencioso: boolean): void {
    const id = this.idEjercicioTrimestre;
    this.consulta?.unsubscribe();
    if (id == null) {
      this.estado = "VACIO";
      this.loading = false;
      this.limpia();
      this.pinta();
      return;
    }
    this.loading = true;
    this.error = "";
    if (!silencioso) {
      this.estado = "CARGANDO";
      this.limpia();
      this.armaCabecera(this.renglon);
    }
    this.pinta();
    this.consulta = this.bscService.revisionDetalle(id).subscribe({
      next: (res) => {
        if (id !== this.idEjercicioTrimestre) {
          return;
        }
        this.loading = false;
        if (!res || res.codigoError) {
          this.falla(mensaje(res, "No se pudo consultar el envío."));
          return;
        }
        this.aplica(res);
        this.pinta();
        this.desplazaSiFalta();
      },
      error: () => {
        if (id !== this.idEjercicioTrimestre) {
          return;
        }
        this.loading = false;
        this.falla("No se pudo consultar el envío: sin conexión con el servidor.");
      },
    });
  }

  private desplazaSiFalta(): void {
    if (this.desplazarAlLlegar) {
      setTimeout(() => this.desplaza());
    }
  }

  private falla(texto: string): void {
    this.error = texto;
    this.estado = "ERROR";
    this.limpia();
    this.armaCabecera(this.renglon);
    this.pinta();
    this.desplazaSiFalta();
  }

  private limpia(): void {
    this.trimestre = null;
    this.ejercicio = null;
    this.ciclo = null;
    this.avisos = [];
    this.trimestres = [];
    this.cifras = [];
    this.notaAlcance = "";
    this.franja = null;
    this.indicadores = [];
    this.bajoMeta = [];
    this.comentarioEnvio = "";
    this.movimientos = [];
    this.acciones = sinAcciones("");
    this.semaforo = null;
  }

  private aplica(res: BscRes): void {
    const t = res.ejercicioTrimestre ?? this.renglon ?? null;
    this.trimestre = t;
    this.ejercicio = res.ejercicio ?? null;
    this.ciclo = res.ciclo ?? null;
    this.avisos = res.avisos ?? [];
    this.puedeAutorizar = res.puedeAutorizar === true;
    this.tienePin = res.tienePin === true;
    this.trimestres = res.trimestres ?? [];
    this.estado = "DATOS";
    this.armaCabecera(t);
    const q = this.q;

    const kpis = (res.kpis ?? []).filter((k) => k.idEjercicioKpi != null && k.estatus !== "RETIRADO");
    const capturas = new Map<number, BscKpiTrimestre>();
    for (const c of res.capturas ?? []) {
      if (c.idEjercicioKpi != null && (c.trimestre == null || c.trimestre === q)) {
        capturas.set(c.idEjercicioKpi, c);
      }
    }
    const meses = new Map<number, Map<number, BscKpiMes>>();
    for (const m of res.meses ?? []) {
      if (m.idEjercicioKpi == null || m.mes == null) {
        continue;
      }
      if (!meses.has(m.idEjercicioKpi)) {
        meses.set(m.idEjercicioKpi, new Map());
      }
      meses.get(m.idEjercicioKpi)?.set(m.mes, m);
    }
    const bajo = new Set((res.kpisBajoMeta ?? []).map((k) => k.idEjercicioKpi));
    this.indicadores = kpis.map((k) =>
      this.indicador(k, q, capturas.get(k.idEjercicioKpi as number) ?? null, meses.get(k.idEjercicioKpi as number), bajo),
    );
    this.ponEscala();
    this.bajoMeta = (res.kpisBajoMeta ?? []).filter((k) => k.idEjercicioKpi != null).map((k) => this.bajoMetaDe(k, q));

    const vigentes = this.indicadores.filter((i) => i.vigente);
    const esperados = vigentes.length * 3;
    const capturados = vigentes.reduce((n, i) => n + i.meses.filter((m) => m.capturado).length, 0);
    const kpisTexto = `${vigentes.length} ${vigentes.length === 1 ? "KPI" : "KPIs"}`;
    const na = this.indicadores.length - vigentes.length;
    const sinAplicar = na > 0 ? ` (${na} sin aplicar en Q${q})` : "";
    this.subtitulo = [
      this.textoEnvio(t),
      `${kpisTexto}${sinAplicar}`,
      esperados > 0 ? `${capturados} de ${esperados} meses capturados` : "",
    ]
      .filter(Boolean)
      .join(" · ");

    this.cifras = this.armaCifras(t);
    this.franja = this.armaFranja(t, res.historial ?? []);
    this.comentarioEnvio = (t?.comentarioEnvio ?? "").trim();
    this.movimientos = (res.historial ?? []).slice().reverse().map((h, i) => movimiento(h, i));
    this.acciones = this.armaAcciones(t);
  }

  /**
   * Una escala común a las barras de la lista (escalaBarras, como A2): con un
   * KPI arriba de 100 % las demás se acortan en proporción en vez de topar.
   */
  private ponEscala(): void {
    const barras = this.indicadores.map((i) => i.barra).filter((b): b is BarraKpi => b != null);
    const escala = escalaBarras(barras);
    for (const b of barras) {
      b.pista = enPista(b, escala);
    }
  }

  /* ============================ Presentación ============================ */

  get q(): number {
    return this.trimestre?.trimestre ?? this.renglon?.trimestre ?? 1;
  }

  get anioEnvio(): number | null {
    return this.trimestre?.anio ?? this.renglon?.anio ?? this.anio;
  }

  get nombreArea(): string {
    return (this.trimestre?.area ?? this.renglon?.area ?? this.ejercicio?.area ?? "").trim() || "El área";
  }

  get idBscArea(): number | null {
    return this.trimestre?.idBscArea ?? this.ejercicio?.idBscArea ?? this.renglon?.idBscArea ?? null;
  }

  get queryArea(): Record<string, string | number | null> {
    return { anio: this.anioEnvio, corte: `${this.q}` };
  }

  get queryKpi(): Record<string, number> {
    return { q: this.q };
  }

  get movimientosVisibles(): Movimiento[] {
    return this.verTodaBitacora ? this.movimientos : this.movimientos.slice(0, BITACORA_CORTA);
  }

  /** El campo del comentario solo cuando hay algo que confirmar con él (no con Reabrir bloqueado). */
  get pideComentario(): boolean {
    return this.acciones.visibles && (this.acciones.modo === "RESOLVER" || this.acciones.reabrir);
  }

  get etiquetaComentario(): string {
    return this.acciones.modo === "REABRIR" ? "Motivo de la reapertura" : "Comentario para el área";
  }

  get ayudaComentario(): string {
    return this.acciones.modo === "REABRIR"
      ? "Obligatorio para reabrir; se confirma con tu PIN."
      : "Obligatorio para regresar; opcional al autorizar. Se confirma con tu PIN.";
  }

  alternaEvidencia(fila: FilaIndicador): void {
    if (this.abiertos.has(fila.idEjercicioKpi)) {
      this.abiertos.delete(fila.idEjercicioKpi);
    } else {
      this.abiertos.add(fila.idEjercicioKpi);
    }
    this.pinta();
  }

  private armaCabecera(t: BscEjercicioTrimestre | null): void {
    const q = t?.trimestre ?? this.q;
    this.titulo = `${(t?.area ?? "").trim() || this.nombreArea} · cierre Q${q}`;
    this.estatus = ESTATUS_TRIMESTRE.find((e) => e.valor === t?.estatus) ?? null;
    this.sinEnviar = t?.sinEnviar === true;
    this.semaforo = SEMAFORO.find((s) => s.valor === t?.semaforo) ?? null;
    if (this.estado !== "DATOS") {
      this.subtitulo = t ? this.textoEnvio(t) : "";
    }
  }

  /** "Enviado por Ana López el 3 jul" / "Sin enviar · venció el 15 jul" / "Importado autorizado del FOR-011". */
  private textoEnvio(t: BscEjercicioTrimestre | null): string {
    if (!t) {
      return "";
    }
    if (t.estatus === "AUTORIZADO" && t.origen === "IMPORTACION") {
      return "Importado autorizado del FOR-011";
    }
    if (t.fechaEnvio || t.usuarioEnvia) {
      const quien = (t.usuarioEnvia ?? "").trim();
      const cuando = t.fechaEnvio ? diaMes(t.fechaEnvio, this.anioEnvio) : "";
      return accionPorQuienYCuando("Enviado", quien, cuando);
    }
    const fecha = t.fechaLimiteEnvio ? diaMes(t.fechaLimiteEnvio, this.anioEnvio) : "";
    const limite = textoVence(fecha, t.sinEnviar);
    return `Sin enviar · ${limite}`;
  }

  private armaCifras(t: BscEjercicioTrimestre | null): Cifra[] {
    const q = this.q;
    const real = numero(t?.alcanceReal);
    const prog = numero(t?.alcanceProgramado);
    const autorizado = t?.estatus === "AUTORIZADO";
    this.notaAlcance = notaDelAlcance(real, autorizado);
    const bajo = this.bajoMeta.length;
    return [
      {
        etiqueta: `Alcance Q${q}`,
        valor: real == null ? SIN_DATO : pct(real),
        nota: real != null && prog != null ? `${pts(real - prog)} vs programado` : "",
        clase: "",
      },
      { etiqueta: "Programado", valor: prog == null ? SIN_DATO : pct(prog), nota: "acumulado al trimestre", clase: "" },
      {
        etiqueta: "KPIs bajo lo programado",
        valor: `${bajo}`,
        nota: `verde desde ${pct(this.verde)}`,
        clase: bajo > 0 ? "is-aviso" : "",
      },
    ];
  }

  private get verde(): number {
    return numero(this.ciclo?.semaforoVerde) ?? VERDE_POR_OMISION;
  }

  private armaFranja(t: BscEjercicioTrimestre | null, historial: BscHistorial[]): Franja | null {
    if (!t) {
      return null;
    }
    const cuando = (fecha: string | undefined) => (fecha ? diaMes(fecha, this.anioEnvio) : "");
    const quien = (t.usuarioResuelve ?? "").trim();
    // Entre comillas, sin el punto final: la frase pone el suyo después («…».).
    const motivo = sinPunto(t.motivo);
    const cita = motivo ? `: «${motivo}»` : "";
    switch (t.estatus) {
      case "AUTORIZADO":
        if (t.origen === "IMPORTACION") {
          return { icono: "verified", texto: "Se importó autorizado al cargar el FOR-011: su alcance quedó congelado.", clase: "is-ok" };
        }
        return {
          icono: "verified",
          texto:
            accionPorQuienYCuando("Autorizado", quien, cuando(t.fechaResolucion)) +
            `${cita}. Su alcance quedó congelado.`,
          clase: "is-ok",
        };
      case "REGRESADO":
        return {
          icono: "undo",
          texto:
            accionPorQuienYCuando("Regresado", quien, cuando(t.fechaResolucion)) +
            `${cita}. El área lo corrige y lo vuelve a enviar; regresa a esta bandeja por revisar.`,
          clase: "is-alerta",
        };
      case "EN_CAPTURA":
        return this.franjaEnCaptura(t, historial);
      case "PENDIENTE": {
        const anterior = this.q > 1 ? `Q${this.q - 1}` : "la planeación";
        return {
          icono: "schedule",
          texto:
            `Todavía no se abre a cierre: se abre cuando se autoriza ${anterior}. ` +
            "Sus meses ya se pueden capturar.",
          clase: "",
        };
      }
      default:
        return null;
    }
  }

  /** En captura: reabierto por Dirección (la última transición de la bitácora) o todavía sin enviar. */
  private franjaEnCaptura(t: BscEjercicioTrimestre, historial: BscHistorial[]): Franja {
    // La última transición del trimestre (la bitácora llega del más viejo al más nuevo).
    const ultimo = ultimoQue(historial, (h) => TRANSICIONES.has(h.accion ?? ""));
    if (ultimo?.accion === "REAPERTURA_Q") {
      const por = (ultimo.usuario ?? "").trim();
      const fecha = ultimo.fecha ? diaMes(ultimo.fecha, this.anioEnvio) : "";
      const comentario = ultimo.comentario ? `: «${sinPunto(ultimo.comentario)}»` : "";
      return {
        icono: "lock_open",
        texto:
          accionPorQuienYCuando("Reabierto a captura", por, fecha) +
          `${comentario}. Se vuelve a revisar cuando el área lo envíe.`,
        clase: "is-alerta",
      };
    }
    return {
      icono: t.sinEnviar ? "event_busy" : "edit_calendar",
      texto: `${this.nombreArea} todavía no lo envía a revisión (${this.textoLimite(t)}). Se cierra en Mi BSC.`,
      clase: t.sinEnviar ? "is-alerta" : "",
    };
  }

  private textoLimite(t: BscEjercicioTrimestre): string {
    const fecha = t.fechaLimiteEnvio ? diaMes(t.fechaLimiteEnvio, this.anioEnvio) : "";
    if (!fecha) {
      return "sin fecha límite";
    }
    return t.sinEnviar ? `la fecha límite fue el ${fecha}` : `fecha límite ${fecha}`;
  }

  private armaAcciones(t: BscEjercicioTrimestre | null): Acciones {
    const modo: ModoAccion | null = modoDe(t?.estatus);
    if (!t || !modo) {
      return sinAcciones("");
    }
    const q = this.q;
    if (!this.puedeAutorizar) {
      return sinAcciones(
        modo === "RESOLVER"
          ? "Autorizar o regresar lo hace Dirección, con la llave «BSC · Autorizar» y su PIN. Tú puedes revisar el envío."
          : "Reabrir un trimestre autorizado lo hace Dirección, con la llave «BSC · Autorizar» y su PIN.",
        modo,
      );
    }
    if (this.ciclo?.estatus === "CERRADO") {
      return sinAcciones(`El año ${this.anioEnvio ?? ""} está cerrado: sus trimestres ya no cambian.`, modo);
    }
    const acciones: Acciones = {
      modo,
      visibles: true,
      nota: "",
      autorizar: true,
      porQueNoAutoriza: "",
      reabrir: true,
      porQueNoReabre: "",
    };
    if (modo === "RESOLVER" && t.esPropio === true) {
      acciones.autorizar = false;
      acciones.porQueNoAutoriza =
        `Lo enviaste tú o estás asignado a ${this.nombreArea}: lo autoriza otra persona con la llave «BSC · Autorizar». ` +
        "Regresarlo con comentarios sí puedes.";
    }
    if (modo === "REABRIR") {
      const siguiente = this.trimestres.find((x) => x.trimestre === q + 1);
      if (this.ejercicio?.origen === "IMPORTACION_HISTORICA") {
        acciones.reabrir = false;
        acciones.porQueNoReabre = "Es de un FOR-011 histórico importado: sus trimestres no tienen flujo y no se reabren.";
      } else if (siguiente?.estatus === "ENVIADO") {
        acciones.reabrir = false;
        acciones.porQueNoReabre = `Q${q + 1} ya está enviado a revisión: primero regrésalo y después reabre Q${q}.`;
      } else if (siguiente?.estatus === "AUTORIZADO") {
        acciones.reabrir = false;
        acciones.porQueNoReabre = `Q${q + 1} ya está autorizado: primero reábrelo y después reabre Q${q}.`;
      }
    }
    return acciones;
  }

  private indicador(
    k: BscEjercicioKpi,
    q: number,
    captura: BscKpiTrimestre | null,
    meses: Map<number, BscKpiMes> | undefined,
    bajo: Set<number | undefined>,
  ): FilaIndicador {
    const id = k.idEjercicioKpi as number;
    const objetivo = (k.objetivo ?? "").trim() || "KPI sin objetivo";
    const vigente = aplicaEn(k, q);
    const semaforo = vigente ? SEMAFORO.find((s) => s.valor === semaforoEn(k, q)) ?? null : null;
    let nota = "";
    if (!vigente) {
      const inicio = k.trimestreInicio;
      nota = inicio != null && inicio > q ? `NA · inicia en Q${inicio}` : `NA · no aplica en Q${q}`;
    }
    const barra = barraDe(k, q, objetivo, vigente, semaforo);
    const avance = vigente
      ? avanceDe(k, q, captura)
      : "No aplica en el trimestre: solo registra actividad.";
    return {
      idEjercicioKpi: id,
      objetivo,
      vigente,
      nota,
      barra,
      semaforo,
      bajoMeta: bajo.has(id),
      avance,
      recursos: (captura?.recursos ?? "").trim(),
      comentario: (captura?.comentario ?? "").trim(),
      meses: mesesDeTrimestre(q).map((mes) => this.mes(mes, meses?.get(mes), vigente)),
      etiquetaAbrir: `Evidencia de ${objetivo}`,
    };
  }

  private mes(mes: number, m: BscKpiMes | undefined, vigente: boolean): MesEnvio {
    const estado = ESTADO_MES.find((e) => e.valor === m?.estado) ?? null;
    // Sin separador "----" todo el texto del FOR-011 quedó en actividad (§6 paso 5), como en A3.
    const actividad = (m?.actividad ?? "").trim() || (m?.evidencia ? "" : (m?.textoOriginal ?? "").trim());
    const evidencia = (m?.evidencia ?? "").trim();
    const capturado = m?.id != null && (actividad !== "" || evidencia !== "");
    const fecha = m?.fechaModificacion || m?.fechaCreacion;
    const vacio = capturado ? "" : vacioDelMes(m, vigente, estado);
    const cuando = fecha ? ` · ${diaMes(fecha, this.anioEnvio)}` : "";
    return {
      mes,
      nombre: nombreMes(mes),
      // El mes de un trimestre enviado es BLOQUEADO; si se capturó, se dice capturado.
      estado: capturado && m?.estado === "BLOQUEADO" ? ESTADO_MES.find((e) => e.valor === "CAPTURADO") ?? estado : estado,
      actividad,
      evidencia,
      capturado,
      capturo:
        capturado && (m?.usuarioAlta || fecha)
          ? `Capturó ${(m?.usuarioAlta ?? "").trim() || SIN_DATO}${cuando}`
          : "",
      vacio,
    };
  }

  private bajoMetaDe(k: BscEjercicioKpi, q: number): IndicadorBajoMeta {
    const l = acumRealEn(k, q);
    const n = acumProgEn(k, q);
    return {
      idEjercicioKpi: k.idEjercicioKpi as number,
      objetivo: (k.objetivo ?? "").trim() || "KPI sin objetivo",
      texto: `Acumulado ${pct(l)} de ${pct(n)} programado: ${pct(ratio(l, n))} de lo programado (verde desde ${pct(this.verde)}).`,
    };
  }

  /* ============================ Autorizar · Regresar · Reabrir ============================ */

  autorizar(): void {
    const t = this.trimestre;
    if (!t?.id || this.acciones.modo !== "RESOLVER" || !this.acciones.autorizar) {
      return;
    }
    const id = t.id;
    const q = this.q;
    const siguiente = this.trimestres.find((x) => x.trimestre === q + 1);
    const abre = queAbre(q, siguiente);
    const alcance = numero(t.alcanceReal) != null ? ` (${pct(t.alcanceReal)} de ${pct(t.alcanceProgramado)} programado)` : "";
    this.abreDialogo(
      {
        aprobar: true,
        eyebrow: "Autorizar trimestre",
        titulo: `${this.nombreArea} · Q${q} ${this.anioEnvio ?? ""}`,
        detalle: `El alcance del área queda congelado${alcance} y ya no cambia aunque se recapture. ${abre}`,
        textoConfirmar: `Autorizar Q${q}`,
        comentarioInicial: this.comentario,
        enviar: (pin, comentario) => this.bscService.revisionResolver(id, true, comentario || null, pin),
      },
      `Q${q} ${this.anioEnvio ?? ""} de ${this.nombreArea} quedó autorizado: su alcance se congeló. ${abre}`,
      "Autorizado",
    );
  }

  regresar(): void {
    const t = this.trimestre;
    if (!t?.id || this.acciones.modo !== "RESOLVER") {
      return;
    }
    const id = t.id;
    const q = this.q;
    this.abreDialogo(
      {
        aprobar: false,
        eyebrow: "Regresar trimestre",
        titulo: `${this.nombreArea} · Q${q} ${this.anioEnvio ?? ""}`,
        detalle:
          "Vuelve al área para corregirlo y enviarlo de nuevo; el comentario queda en la bitácora y le llega en la campana del COS.",
        textoConfirmar: "Regresar",
        comentarioInicial: this.comentario,
        enviar: (pin, comentario) => this.bscService.revisionResolver(id, false, comentario || null, pin),
      },
      `Q${q} ${this.anioEnvio ?? ""} de ${this.nombreArea} se regresó al área con tu comentario.`,
      "Regresado",
    );
  }

  reabrir(): void {
    const t = this.trimestre;
    if (!t?.id || this.acciones.modo !== "REABRIR" || !this.acciones.reabrir) {
      return;
    }
    const id = t.id;
    const q = this.q;
    this.abreDialogo(
      {
        aprobar: false,
        icono: "lock_open",
        comentario: MOTIVO_REAPERTURA,
        eyebrow: "Reabrir trimestre",
        titulo: `${this.nombreArea} · Q${q} ${this.anioEnvio ?? ""}`,
        detalle:
          `Q${q} vuelve a captura y su alcance congelado se anula: se calcula en vivo hasta que se vuelva a autorizar.` +
          (q < 4 ? ` Mientras, Q${q + 1} no se puede enviar.` : ""),
        textoConfirmar: `Reabrir Q${q}`,
        comentarioInicial: this.comentario,
        enviar: (pin, motivo) => this.bscService.revisionReabrir(id, motivo, pin),
      },
      `Q${q} ${this.anioEnvio ?? ""} de ${this.nombreArea} volvió a captura: su alcance se calcula en vivo hasta que se vuelva a autorizar.`,
      "Reabierto",
    );
  }

  /**
   * Abre el cuadro del PIN y aplica lo que conteste. Un error que no es del
   * PIN (otro ya lo resolvió: VAL-BSC-CONFLICTO; el año se cerró; no es
   * suyo: VAL-BSC-PROPIA) llega al cancelar: se dice aquí y el envío y la
   * bandeja se vuelven a pedir para enseñar cómo quedó.
   */
  private abreDialogo(
    datos: Parameters<typeof abrirDialogoPinBsc>[1],
    textoExito: string,
    tituloExito: string,
  ): void {
    if (this.dialogo) {
      return;
    }
    const id = this.idEjercicioTrimestre;
    this.errorAccion = "";
    this.dialogo = abrirDialogoPinBsc(this.dialog, { ...datos, cerrarAnte: ERRORES_DE_RECARGA });
    this.dialogo.afterClosed().subscribe((res) => {
      this.dialogo = undefined;
      if (!res || this.destruido || id !== this.idEjercicioTrimestre) {
        this.enfoca(".envio-bsc__botones button:not(:disabled), #envio-bsc-titulo, #envio-bsc-titulo-vacio");
        return;
      }
      if (res.codigoError) {
        if (res.codigoError === "AUTH-001") {
          this.errorAccion = "Tu sesión caducó. Vuelve a entrar.";
          this.pinta();
          return;
        }
        // El servidor pide recargar: la pantalla ya lo hace.
        const texto = (res.mensajeError || "No se pudo resolver el trimestre.").replace(/(?:[;.]|(^|[^\s;.]))\s*recarga la página\.?/i, "$1.");
        this.errorAccion = `${texto} Se volvió a consultar el envío: así quedó.`;
        this.consultar(true);
        this.cambio.emit({ resuelto: false });
        this.enfoca(".envio-bsc__error-accion");
        return;
      }
      this.comentario = "";
      this.aplica(res);
      this.pinta();
      this.cambio.emit({ resuelto: true, trimestre: res.ejercicioTrimestre });
      this.enfoca("#envio-bsc-titulo, #envio-bsc-titulo-vacio");
      this.msg.successCall(textoExito, tituloExito, null);
    });
  }

  /* ============================ trackBy ============================ */

  trackIndicador(_: number, f: FilaIndicador): number {
    return f.idEjercicioKpi;
  }

  trackMes(_: number, m: MesEnvio): number {
    return m.mes;
  }

  trackMovimiento(_: number, m: Movimiento): string {
    return m.clave;
  }

  trackTexto(i: number): number {
    return i;
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.destruido) {
      this.cdr.detectChanges();
    }
  }

  /** Después de pintar: el primero de los selectores que exista y no esté deshabilitado. */
  private enfoca(selectores: string): void {
    setTimeout(() => {
      for (const s of selectores.split(",")) {
        const el = this.host.nativeElement.querySelector<HTMLElement>(s.trim());
        if (el) {
          el.focus();
          return;
        }
      }
    });
  }
}

/* ============================ Funciones puras ============================ */

function sinAcciones(nota: string, modo: ModoAccion | null = null): Acciones {
  return { modo, visibles: false, nota, autorizar: false, porQueNoAutoriza: "", reabrir: false, porQueNoReabre: "" };
}

function sinPunto(texto: string | null | undefined): string {
  return (texto ?? "").trim().replace(/(^|[^.\s])[.\s]+$/, "$1");
}

function claseBarra(semaforo: SemaforoBsc | null): string {
  switch (semaforo) {
    case "VERDE":
      return "is-verde";
    case "AMARILLO":
      return "is-amarillo";
    case "ROJO":
      return "is-rojo";
    default:
      return "";
  }
}

function mensaje(res: BscRes | null | undefined, porOmision: string): string {
  // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
  if (res?.codigoError === "AUTH-001") {
    return "Tu sesión caducó. Vuelve a entrar.";
  }
  return res?.mensajeError || porOmision;
}

/** Un renglón de la bitácora: lo que pasó, quién y cuándo (lo más reciente primero), como C1. */
function movimiento(h: BscHistorial, i: number): Movimiento {
  const accion = (h.accion && ACCION_HISTORIAL[h.accion]) || h.accion || SIN_DATO;
  const partes = h.accion && COMENTARIO_DEL_SISTEMA.has(h.accion) ? partesDelSistema(h) : partesDePersona(h);
  const id = h.id ?? `i${i}`;
  return {
    clave: `${id}`,
    cuando: fechaHora(h.fecha),
    accion,
    quien: (h.usuario ?? "").trim() || (h.idUsuario != null ? `Usuario ${h.idUsuario}` : SIN_DATO),
    detalle: partes.join(" · "),
  };
}

/** Lo que escribió el sistema (captura del mes o del avance): su comentario o el mes, y el valor nuevo. */
function partesDelSistema(h: BscHistorial): string[] {
  const partes: string[] = [];
  if (h.comentario) {
    partes.push(h.comentario);
  } else if (h.mes) {
    partes.push(mesEnTexto(h.mes));
  }
  if (h.valorNuevo) {
    partes.push(h.valorNuevo);
  }
  return partes;
}

/** Lo que hizo una persona: el cambio de valor y su comentario entre comillas. */
function partesDePersona(h: BscHistorial): string[] {
  const partes: string[] = [];
  if (h.valorAnterior || h.valorNuevo) {
    partes.push(h.valorAnterior && h.valorNuevo ? `${h.valorAnterior} → ${h.valorNuevo}` : (h.valorNuevo ?? h.valorAnterior ?? ""));
  }
  if (h.comentario) {
    partes.push(`«${h.comentario}»`);
  }
  return partes;
}

/** "Autorizado por Ana López el 5 jul": la acción con quién y cuándo, si se saben. */
function accionPorQuienYCuando(accion: string, quien: string, cuando: string): string {
  const porQuien = quien ? ` por ${quien}` : "";
  const elDia = cuando ? ` el ${cuando}` : "";
  return `${accion}${porQuien}${elDia}`;
}

/** "sin fecha límite", "venció el 15 jul" o "vence el 15 jul". */
function textoVence(fecha: string, sinEnviar: boolean | undefined): string {
  if (!fecha) {
    return "sin fecha límite";
  }
  return sinEnviar ? `venció el ${fecha}` : `vence el ${fecha}`;
}

/** De dónde sale el alcance del trimestre: sin avances, congelado al autorizar o en vivo. */
function notaDelAlcance(real: number | null, autorizado: boolean): string {
  if (real == null) {
    return "Sin alcance: el trimestre no tiene avances capturados.";
  }
  return autorizado
    ? "Congelado al autorizar (snapshot): ya no cambia aunque se recapture."
    : "Calculado en vivo con lo capturado; se congela al autorizar.";
}

/** Lo que se hace con el trimestre: resolver el ENVIADO, reabrir el AUTORIZADO; nada con otro estatus. */
function modoDe(estatus: EstatusTrimestreBsc | undefined): ModoAccion | null {
  if (estatus === "ENVIADO") {
    return "RESOLVER";
  }
  return estatus === "AUTORIZADO" ? "REABRIR" : null;
}

/**
 * La barra del KPI: su acumulado real con la raya de lo programado, en la
 * escala 1 hasta que ponEscala le pone la de la lista; nula si no aplica o
 * no tiene acumulado.
 */
function barraDe(
  k: BscEjercicioKpi,
  q: number,
  objetivo: string,
  vigente: boolean,
  semaforo: EstadoBsc<SemaforoBsc> | null,
): BarraKpi | null {
  const l = acumRealEn(k, q);
  const n = acumProgEn(k, q);
  if (!vigente || l == null) {
    return null;
  }
  const deProgramado = n != null ? ` de ${pct(n)} programado` : "";
  const enSemaforo = semaforo ? `, ${semaforo.texto}` : "";
  return {
    real: l,
    programado: n,
    pista: enPista({ real: l, programado: n }, 1),
    clase: claseBarra(semaforoEn(k, q)),
    texto: pct(l),
    accesible: `Acumulado de ${objetivo} al cierre de Q${q}: ${pct(l)}${deProgramado}` + enSemaforo,
  };
}

/** "Real 90% · programado 100% · peso 25% · ponderado 22.5%", y las unidades si las hay. */
function avanceDe(k: BscEjercicioKpi, q: number, captura: BscKpiTrimestre | null): string {
  const real = numero(captura?.avanceReal);
  const programado = numero(captura?.avanceProgramado);
  const unidades = captura?.valorUnidades != null ? ` · ${captura.valorUnidades} ${unidad(k.unidad)}` : "";
  return [
    `Real ${real == null ? "sin capturar" : pct(real)}`,
    `programado ${programado == null ? "100%" : pct(programado)}`,
    `peso ${pct(pesoEn(k, q))}`,
    `ponderado ${pct(numero(captura?.ponderado) ?? ponderadoEn(k, q))}`,
  ].join(" · ") + unidades;
}

/** Lo que se dice de un mes sin captura. */
function vacioDelMes(m: BscKpiMes | undefined, vigente: boolean, estado: EstadoBsc<EstadoMesBsc> | null): string {
  if (!vigente || m?.estado === "NO_APLICA") {
    // El badge «No aplica» ya lo dice.
    return estado ? "" : "No aplica en este trimestre.";
  }
  if (m?.estado === "VENCIDO") {
    return "Sin captura y ya venció.";
  }
  if (m?.estado === "BLOQUEADO") {
    return "Se envió sin captura de este mes.";
  }
  return "Sin captura todavía.";
}

/** Lo que pasa con el trimestre siguiente al autorizar este. */
function queAbre(q: number, siguiente: BscEjercicioTrimestre | undefined): string {
  if (q >= 4) {
    return "Es el último trimestre del año.";
  }
  return siguiente?.estatus === "PENDIENTE" || !siguiente ? `Q${q + 1} se abre a captura.` : `Q${q + 1} sigue en captura.`;
}

/** El último de la lista que cumple, como filter(…).pop() sin la lista intermedia; undefined si ninguno. */
function ultimoQue<T>(lista: T[], cumple: (x: T) => unknown): T | undefined {
  for (let i = lista.length - 1; i >= 0; i--) {
    if (cumple(lista[i])) {
      return lista[i];
    }
  }
  return undefined;
}
